// packfile.ts ↔ mpshared/packfile.cpp — the pack-file directory + c-runtime-style
// stream API over pdb records.
//
// Record 0 of every pack is a DIRECTORY: fixed 32-byte entries
// { char szFn[29]; byte cRecs; word nRecFirst (BE) }, sorted ascending by
// lowercase name (FindDirEntry binary-searches it). A named file's bytes are the
// concatenation of cRecs records starting at nRecFirst. Readers stack: the most
// recently Pushed pack wins name lookups (DLC packs shadow base content).

import { MemPdbReader } from './pdbreader.ts';
import { md5bytes } from '../../../workers/leaderboard/src/md5.ts';

const kcbFilename = 29; // packfile.h:66
const kcbDirEntry = 32;

export interface DirEntry {
  szFn: string;
  cRecs: number;
  nRecFirst: number;
}

interface ReaderInfo {
  // packfile.h:79 (pvCookie/cOpen bookkeeping is GC-collapsed)
  ppdbReader: MemPdbReader;
  pdir: DirEntry[];
  pszDir: string;
  pszFn: string;
}

/** packfile.h:92 File — the c-runtime-style stream state incl. per-record sizes. */
export class PackFile {
  readonly prnfo: ReaderInfo;
  readonly cRecs: number;
  readonly nRecFirst: number;
  readonly acb: number[];
  readonly cbTotal: number;
  offRecStart = 0;
  offStream = 0;
  nRecOffStream = 0;

  constructor(prnfo: ReaderInfo, cRecs: number, nRecFirst: number, acb: number[], cbTotal: number) {
    this.prnfo = prnfo;
    this.cRecs = cRecs;
    this.nRecFirst = nRecFirst;
    this.acb = acb;
    this.cbTotal = cbTotal;
  }
}

export const SEEK_SET = 0;
export const SEEK_CUR = 1;
export const SEEK_END = 2;

export class PackFileReader {
  private m_arnfo: ReaderInfo[] = [];

  // packfile.cpp:457-516 — parse record 0 as the directory and stack the reader
  Push(pszDir: string | null, pszFn: string, ppdbReader: MemPdbReader): boolean {
    const pb = ppdbReader.MapRecord(0);
    if (pb === null) return false;
    const cEntries = Math.floor(pb.length / kcbDirEntry);
    const pdir: DirEntry[] = [];
    for (let i = 0; i < cEntries; i++) {
      const off = i * kcbDirEntry;
      let szFn = '';
      for (let j = 0; j < kcbFilename && pb[off + j] !== 0; j++) {
        szFn += String.fromCharCode(pb[off + j]);
      }
      if (szFn.length === 0) continue; // (the vendored tool skips blanks too)
      pdir.push({
        szFn,
        cRecs: pb[off + kcbFilename],
        nRecFirst: (pb[off + kcbFilename + 1] << 8) | pb[off + kcbFilename + 2],
      });
    }
    // pszDir == null -> split pszFn into dir + basename (packfile.cpp:496-506)
    let dir = pszDir;
    let fn = pszFn;
    if (dir === null) {
      const slash = pszFn.lastIndexOf('/');
      dir = slash < 0 ? '' : pszFn.slice(0, slash);
      fn = slash < 0 ? pszFn : pszFn.slice(slash + 1);
    }
    this.m_arnfo.push({ ppdbReader, pdir, pszDir: dir, pszFn: fn });
    return true;
  }

  // packfile.cpp:518-526
  Pop(): boolean {
    if (this.m_arnfo.length === 0) return false;
    this.RemoveReader(this.m_arnfo.length - 1);
    return true;
  }

  // packfile.cpp:528-547
  private RemoveReader(rnfo: number): void {
    this.m_arnfo[rnfo].ppdbReader.Close();
    this.m_arnfo.splice(rnfo, 1);
  }

  /** packfile.cpp:549-571 minus the host DeletePdb (storage removal is the
   *  embedder's concern — IndexedDB pack store). Returns false when in-use
   *  semantics would have blocked... which the GC port no longer tracks; the
   *  C++ check guarded manual lifetime only. */
  Delete(pszDir: string | null, pszFn: string): boolean {
    for (let n = 0; n < this.m_arnfo.length; n++) {
      const prnfo = this.m_arnfo[n];
      if (pszDir !== null && pszDir.toLowerCase() !== prnfo.pszDir.toLowerCase()) continue;
      if (pszFn.toLowerCase() !== prnfo.pszFn.toLowerCase()) continue;
      this.RemoveReader(n);
      break;
    }
    return true;
  }

  // packfile.cpp:403-453 — lowercase the name; binary search each directory,
  // most recently pushed reader first
  FindDirEntry(psz: string): { dir: DirEntry; prnfo: ReaderInfo } | null {
    const szT = psz.toLowerCase();
    for (let n = this.m_arnfo.length - 1; n >= 0; n--) {
      const prnfo = this.m_arnfo[n];
      let nMin = 0;
      let nMax = prnfo.pdir.length - 1;
      while (nMax >= nMin) {
        const nCurrent = nMin + ((nMax - nMin) >> 1);
        const pdirT = prnfo.pdir[nCurrent];
        const cmp = szT < pdirT.szFn ? -1 : szT > pdirT.szFn ? 1 : 0;
        if (cmp === 0) return { dir: pdirT, prnfo };
        if (cmp > 0) nMin = nCurrent + 1;
        else nMax = nCurrent - 1;
      }
    }
    return null;
  }

  // packfile.cpp:63-115
  fopen(pszFn: string, pszMode: string): PackFile | null {
    if (pszMode !== 'rb') return null;
    const found = this.FindDirEntry(pszFn);
    if (found === null) return null;
    const { dir, prnfo } = found;
    const acb: number[] = [];
    let cbTotal = 0;
    for (let nRec = dir.nRecFirst; nRec < dir.nRecFirst + dir.cRecs; nRec++) {
      const cbT = prnfo.ppdbReader.GetRecordSize(nRec) ?? 0;
      cbTotal += cbT;
      acb.push(cbT);
    }
    return new PackFile(prnfo, dir.cRecs, dir.nRecFirst, acb, cbTotal);
  }

  // packfile.cpp:117-127
  fclose(_pfil: PackFile): number {
    return 0;
  }

  // packfile.cpp:129-159 — stream read across record boundaries
  fread(pv: Uint8Array, cb: number, c: number, pfil: PackFile): number {
    let cbT = cb * c;
    let ib = 0;
    while (cbT !== 0 && pfil.nRecOffStream < pfil.cRecs) {
      const offRecEnd = pfil.offRecStart + pfil.acb[pfil.nRecOffStream];
      const cbLeft = offRecEnd - pfil.offStream;
      const cbReadNext = cbT < cbLeft ? cbT : cbLeft;
      pfil.prnfo.ppdbReader.ReadRecord(
        pfil.nRecFirst + pfil.nRecOffStream,
        pfil.offStream - pfil.offRecStart,
        cbReadNext,
        pv.subarray(ib, ib + cbReadNext),
      );
      cbT -= cbReadNext;
      ib += cbReadNext;
      pfil.offStream += cbReadNext;
      if (pfil.offStream === offRecEnd && pfil.nRecOffStream < pfil.cRecs) {
        pfil.offRecStart = offRecEnd;
        pfil.nRecOffStream++;
      }
    }
    if (cbT === 0) return c;
    return Math.floor((cb * c - cbT) / cb);
  }

  // packfile.cpp:161-253 — seek with the record-walk bookkeeping
  fseek(pfil: PackFile, delta: number, nOrigin: number): number {
    let offNew: number;
    switch (nOrigin) {
      case SEEK_CUR:
        offNew = pfil.offStream + delta;
        break;
      case SEEK_END:
        offNew = pfil.cbTotal + delta;
        break;
      case SEEK_SET:
        offNew = delta;
        break;
      default:
        return 1;
    }
    if (offNew < 0 || offNew > pfil.cbTotal) return 1;

    // Special supported case for seeking to the end
    if (offNew === pfil.cbTotal) {
      if (pfil.cbTotal === 0) return 0;
      while (pfil.nRecOffStream < pfil.cRecs - 1) {
        pfil.offRecStart += pfil.acb[pfil.nRecOffStream];
        pfil.nRecOffStream++;
      }
      pfil.offStream = offNew;
      return 0;
    }

    // Special case for seeking from the end backwards
    if (pfil.offStream === pfil.cbTotal) {
      if (pfil.nRecOffStream === pfil.cRecs) {
        pfil.nRecOffStream--;
        pfil.offRecStart -= pfil.acb[pfil.nRecOffStream];
      }
    }

    if (offNew < pfil.offRecStart + pfil.acb[pfil.nRecOffStream]) {
      if (offNew < pfil.offRecStart) {
        // The new pos is in a previous record
        for (;;) {
          pfil.nRecOffStream--;
          pfil.offRecStart -= pfil.acb[pfil.nRecOffStream];
          if (offNew >= pfil.offRecStart) {
            pfil.offStream = offNew;
            break;
          }
        }
      } else {
        pfil.offStream = offNew;
      }
    } else {
      // The new pos is in a record ahead of the current record
      for (;;) {
        pfil.offRecStart += pfil.acb[pfil.nRecOffStream];
        pfil.nRecOffStream++;
        if (offNew < pfil.offRecStart + pfil.acb[pfil.nRecOffStream]) {
          pfil.offStream = offNew;
          break;
        }
      }
    }
    return 0;
  }

  // packfile.cpp:255-258
  ftell(pfil: PackFile): number {
    return pfil.offStream;
  }

  /** packfile.cpp:260-287 — directory listing of the first/last pushed pack
   *  (the C++ Enum cursor becomes a generator). */
  *EnumFiles(key: 'first' | 'last'): Generator<string> {
    if (this.m_arnfo.length === 0) return;
    const prnfo = this.m_arnfo[key === 'first' ? 0 : this.m_arnfo.length - 1];
    for (const d of prnfo.pdir) yield d.szFn;
  }

  // packfile.cpp:289-355 — whole-file view: multi-record files concatenate
  // through the stream API; single-record files map the record directly
  MapFile(pszFn: string): Uint8Array | null {
    const found = this.FindDirEntry(pszFn);
    if (found === null) return null;
    const { dir, prnfo } = found;
    if (dir.cRecs > 1) {
      const pfil = this.fopen(pszFn, 'rb');
      if (pfil === null) return null;
      this.fseek(pfil, 0, SEEK_END);
      const cb = this.ftell(pfil);
      this.fseek(pfil, 0, SEEK_SET);
      const pb = new Uint8Array(cb);
      if (this.fread(pb, cb, 1, pfil) !== 1) return null;
      return pb;
    }
    return prnfo.ppdbReader.MapRecord(dir.nRecFirst);
  }

  // packfile.cpp:357-371 — GC: views need no unmap; kept for call-site parity
  UnmapFile(_pb: Uint8Array): void {}

  // packfile.cpp:373-387 — pack-content integrity hash (DLC verification)
  HashFile(pszFn: string): Uint8Array | null {
    const pb = this.MapFile(pszFn);
    return pb === null ? null : md5bytes(pb);
  }

  // packfile.cpp:389-401
  GetPdbName(pszFn: string): { pszPdb: string; pszDir: string } | null {
    const found = this.FindDirEntry(pszFn);
    if (found === null) return null;
    return { pszPdb: found.prnfo.pszFn, pszDir: found.prnfo.pszDir };
  }
}
